import React, { useState, useContext } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { auth } from '../../firebase/firebaseConfig';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { AuthContext } from '../AuthContext';
import { createInstructorProfile, createAccessKey } from '../../firebase/db';

const ProfessorSignup = () => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [institution, setInstitution] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { currentUser, loading: authLoading } = useContext(AuthContext);

    if (authLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', color: '#666' }}>
                Loading...
            </div>
        );
    }

    if (currentUser) {
        return <Navigate to="/professor-dashboard" replace />;
    }

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');

        const trimmedName = fullName.trim();
        const trimmedInstitution = institution.trim();

        if (!trimmedName) {
            setError('Please enter your full name.');
            return;
        }

        if (!trimmedInstitution) {
            setError('Please enter your institution.');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await updateProfile(userCredential.user, { displayName: trimmedName });

            await createInstructorProfile(userCredential.user.uid, {
                name: trimmedName,
                email,
                institution: trimmedInstitution,
            });

            // Generate trial access key (90-day expiry, max 3 games)
            const trialKey = 'TRIAL-' + Math.random().toString(36).substring(2, 10).toUpperCase();
            await createAccessKey(trialKey, {
                instructorId: userCredential.user.uid,
                type: 'trial',
                status: 'active',
                activatedAt: null,
                expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
                maxGames: 3,
                metadata: { source: 'signup', institution: trimmedInstitution },
            });

            try {
                await sendEmailVerification(userCredential.user);
            } catch (verifyErr) {
                console.warn('Email verification send failed (non-blocking):', verifyErr);
            }

            navigate('/professor-dashboard');
        } catch (err) {
            console.error('Signup error:', err);
            if (err.code === 'auth/email-already-in-use') {
                setError('An account with this email already exists. Try signing in.');
            } else if (err.code === 'auth/weak-password') {
                setError('Password should be at least 6 characters.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else {
                setError('Signup failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #ddd',
        fontSize: '14px',
        outline: 'none',
    };

    return (
        <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'Arial, sans-serif',
        }}>
            <div style={{
                backgroundColor: '#fff', padding: '40px', borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxWidth: '400px', width: '100%',
            }}>
                <h1 style={{ textAlign: 'center', color: '#4b2e83', marginBottom: '8px', fontSize: '24px' }}>
                    Create Instructor Account
                </h1>
                <p style={{ textAlign: 'center', color: '#666', marginBottom: '24px', fontSize: '14px' }}>
                    Sign up to create and manage simulations
                </p>

                <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Full Name"
                        required
                        style={inputStyle}
                    />
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email"
                        required
                        style={inputStyle}
                    />
                    <input
                        type="text"
                        value={institution}
                        onChange={(e) => setInstitution(e.target.value)}
                        placeholder="Institution (e.g., Kellogg School of Management)"
                        required
                        style={inputStyle}
                    />
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password (min 8 characters)"
                        required
                        style={inputStyle}
                    />
                    <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm Password"
                        required
                        style={inputStyle}
                    />

                    {error && (
                        <p style={{ color: '#e15759', fontSize: '13px', margin: 0, textAlign: 'center' }}>
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '12px', backgroundColor: '#4b2e83', color: '#fff',
                            border: 'none', borderRadius: '6px', fontSize: '16px',
                            fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                        }}
                    >
                        {loading ? 'Creating account...' : 'Create Account'}
                    </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button
                        onClick={() => navigate('/professor-login')}
                        style={{
                            background: 'none', border: 'none', color: '#4b2e83',
                            cursor: 'pointer', fontSize: '13px', textDecoration: 'underline',
                        }}
                    >
                        Already have an account? Sign in
                    </button>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            background: 'none', border: 'none', color: '#4b2e83',
                            cursor: 'pointer', fontSize: '13px', textDecoration: 'underline',
                        }}
                    >
                        Back to Student Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfessorSignup;
